import { PhoneCall, Mail, MessageSquare, QrCode, ArrowUpRight, MapPin } from "lucide-react";
import LandingInquiryCard from "@/components/LandingInquiryCard";
import { ADDRESS_LINE, COMPANY } from "@/lib/company";

const CHANNELS = [
  [QrCode, "Flyer + landing page with its own QR"],
  [Mail, "Email to the decision-maker, opens + clicks tracked"],
  [MessageSquare, "A2P-compliant SMS"],
  [PhoneCall, "AI dialer that qualifies and books the call"],
];

// Sales & advertising contact: what a campaign is (left), the form (right), the sales contact tag at the bottom.
export default function ContactSection({ name, phone, email, inquiryRef }) {
  return (
    <section id="inquire" data-testid="inquiry-form-container" className="relative border-t border-border/40 bg-gradient-to-b from-background to-black">
      <div className="max-w-7xl mx-auto px-4 py-14 sm:py-20">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-neon-magenta" data-testid="contact-kicker">Sales &amp; advertising opportunities · Contact us</div>
            <h2 data-testid="contact-headline" className="mt-3 font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">Tell us what you sell.<br />We'll put it in front of the owners.</h2>
            <p className="mt-3 text-sm text-white/65 leading-relaxed max-w-md">One form, one call back. Your campaign gets a flyer, a landing page and an outreach sequence — like the two running above.</p>
            <ul className="mt-6 grid gap-2 text-xs sm:text-[13px]" data-testid="contact-channels">
              {CHANNELS.map(([Icon, t]) => (
                <li key={t} className="flex items-start gap-2 border border-border/70 bg-black/30 px-3 py-2.5 text-white/85"><Icon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-neon-magenta" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <LandingInquiryCard
              id="campaign"
              accent="magenta"
              kicker="Run a campaign for my business"
              title="What do you sell, and to whom?"
              pitch="Product or service, target venues and states. We come back with the count we can reach and a plan for email, SMS and calls."
              buttonLabel="Send"
              interest="run_my_campaign"
              referralCode={inquiryRef}
              withBusinessType
            />
          </div>
        </div>

        <div className="mt-8 border border-border/70 bg-[#121214]/70 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8" data-testid="sales-contact-card">
          <div>
            <div className="font-heading font-black text-base">{name}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Sales · Arcade Ops</div>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-sm">
            <a href={`tel:${phone}`} data-testid="sales-contact-phone-link" className="flex items-center gap-2 text-neon-yellow hover:text-white transition-colors"><PhoneCall className="w-4 h-4" /> {phone}</a>
            <a href={`mailto:${email}`} data-testid="sales-contact-email-link" className="flex items-center gap-2 text-neon-cyan hover:text-white transition-colors break-all"><Mail className="w-4 h-4 shrink-0" /> {email}</a>
          </div>
          <a href="/login?mode=register" data-testid="database-access-link" className="sm:ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-neon-cyan transition-colors">
            Want an account? Access the database <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>
        <address className="mt-3 not-italic flex items-start gap-2 font-mono text-[11px] text-white/55" data-testid="contact-address">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-neon-cyan" /> {COMPANY.name} · {ADDRESS_LINE}
        </address>
      </div>
    </section>
  );
}
